import React, { useEffect, useState } from "react";
import { Form, Modal, Row, Col, Collapse, Spinner, Button, Image } from "react-bootstrap";
import { addNewRole, getAvailableMenus } from "../../services/role-service";
import { refreshUserData } from "../../services/auth-service";
import { toast } from 'react-toastify';
import downarrowicon from "../../assets/downarrowicon.svg";
import upArrowIcon from "../../assets/uparrowicon.svg";
import InputBox from "../common/InputBox";
import SearchSelect from "../common/SearchSelect";

const RoleForm = ({ show, onHide, action, rowdata, setReload }) => {
  const [open, setOpen] = useState({});
  const [parent, setParent] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [menus, setMenus] = useState({});
  const [availableMenus, setAvailableMenus] = useState({});
  const [error, setError] = useState({});
  const [submit, setSubmit] = useState(false);
  const [fieldCount, setFieldCount] = useState([]);
  const [mandatoryFields, setMandatoryFields] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [roleObj, setRoleObj] = useState(
    action === "Edit" && rowdata
      ? {
          roleCode: rowdata.roleCode,
          roleName: rowdata.roleName,
          status: rowdata.status
        }
      : {
          roleCode: "",
          roleName: "",
          status: "Active"
        }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRoleObj(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    loadAvailableMenus();
  }, []);

  const loadAvailableMenus = async () => {
    try {
      const response = await getAvailableMenus();
      if (response.statusCode === '0000') {
        setAvailableMenus(response.data);
        setMenus(JSON.parse(JSON.stringify(response.data)));
      }
    } catch (error) {
      console.error('Error loading available menus:', error);
      toast.error('Failed to load available menus');
    }
  };

  useEffect(() => {
    if (action === "Edit" && rowdata && Object.keys(availableMenus).length > 0) {
      setEditData();
    }
  }, [action, rowdata, availableMenus]);

  const setEditData = () => { // NOSONAR
    let temp = {};
    for (let key in JSON.parse(JSON.stringify(availableMenus))) {
      temp[key] = [];
      if (!rowdata?.authorizedSessions || !rowdata?.authorizedSessions[key]) {
        temp[key] = JSON.parse(JSON.stringify(availableMenus))[key];
      } else {
        for (let menu1 of JSON.parse(JSON.stringify(availableMenus))[key]) {
          let found = false;
          if (rowdata.authorizedSessions[key]) {
            for (let menu2 of JSON.parse(JSON.stringify(rowdata.authorizedSessions))[key]) {
              if (menu1.submenu === menu2.submenu) {
                temp[key].push(menu2);
                found = true;
                break;
              }
            }
          }
          if (!found) {
            temp[key].push(menu1);
          }
        }
      }
    }
    setMenus(temp);
  };

  useEffect(() => {
    let selectAll = true;
    let tempParent = {};
    for (let parentMenu in menus) {
      tempParent[parentMenu] = true;
      if (menus[parentMenu]) {
        for (let menu of menus[parentMenu]) {
          if (!menu.read) {
            tempParent[parentMenu] = false;
            selectAll = false;
          }
        }
      }
    }
    setSelectAll(selectAll);
    setParent(tempParent);
  }, [menus]);

  const handleChangeSelectAll = () => {
    let temp = menus;
    for (let parentMenu in temp) {
      if (temp[parentMenu].length > 0) {
        for (let menu of temp[parentMenu]) {
          menu.read = !selectAll;
          if (menu.write) {
            menu.read = true;
          }
          if (!menu.write) {
            menu.write = false;
          }
        }
      }
    }
    setMenus(prev => ({
      ...prev,
      ...temp
    }));
    setSelectAll(!selectAll);
  };

  const handleChangeCheckbox = (parentMenu, submenu, type, checked) => {
    let temp = menus[parentMenu];
    for (let menu of temp) {
      if (menu.submenu === submenu) {
        menu[type] = checked;
        if (type === "write" && checked) {
          menu.read = checked;
        }
        if (menu.write) {
          menu.read = true;
        }
        if (!menu.write) {
          menu.write = false;
        }
        break;
      }
    }
    setMenus(prev => ({
      ...prev,
      [parentMenu]: temp
    }));
  };

  const handleParentChangeCheckbox = (parentMenu, checked) => {
    let temp = menus[parentMenu];
    for (let menu of temp) {
      menu.read = checked;
      if (menu.write) {
        menu.read = true;
      }
      if (!menu.write) {
        menu.write = false;
      }
    }
    setMenus(prev => ({
      ...prev,
      [parentMenu]: temp
    }));
  };

  const submitForm = (e) => {
    e.preventDefault();
    
    // Check if role is being deactivated
    if (roleObj.status === 'Inactive' && action === 'Edit' && rowdata?.status === 'Active') {
      setShowConfirmation(true);
    } else {
      setSubmit(submit ? false : true);
    }
  };

  useEffect(() => {
    const fieldValidation = 2;
    if (fieldCount.length >= fieldValidation) {
      setSubmit(submit ? false : true);
      handleSubmit();
      setFieldCount([]);
    }
  }, [fieldCount]);

  const handleSubmit = async () => {
    setLoading(true);

    try {
      let temp = {};
      for (let parentMenu in menus) {
        // Send all menus, not just those with read !== undefined
        let tempArray = menus[parentMenu].map(item => ({
          submenu: item.submenu,
          read: item.read === true,
          write: item.write === true,
          menuUrl: item.menuUrl || ''
        }));
        if (tempArray.length > 0) {
          temp[parentMenu] = tempArray;
        }
      }

      const submitData = {
        ...roleObj,
        authorizedSessions: temp
      };

      const response = await addNewRole(submitData);
      toast.success(response.msg);
      
      // Check if current user's role was updated
      const currentUser = JSON.parse(sessionStorage.getItem('userData') || '{}');
      if (currentUser.role === roleObj.roleCode) {
        // Refresh current user's data and trigger header refresh
        await refreshUserData();
        window.dispatchEvent(new CustomEvent('roleUpdated'));
      }
      
      onHide();
      setReload();
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error('Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeactivation = () => {
    setShowConfirmation(false);
  };

  const handleConfirmDeactivation = () => {
    setShowConfirmation(false);
    setSubmit(submit ? false : true);
  };

  return (
    <>
      {loading && (
        <div className="spinner-box">
          <Spinner animation="border" className="spinner" />
        </div>
      )}
      <Modal show={show} onHide={onHide} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {action} {action === "Add" ? "New" : ""} Role & Authorization
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={submitForm}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <InputBox
                  type="text"
                  label="Role Code"
                  maxLength={50}
                  name="roleCode"
                  value={roleObj.roleCode}
                  onChange={handleChange}
                  required
                  readOnly={action === "Edit"}
                  setError={setError}
                  error={error}
                  submit={submit}
                  setFieldCount={setFieldCount}
                  fieldCount={fieldCount}
                  mandatoryFields={mandatoryFields}
                  setMandatoryFields={setMandatoryFields}
                />
              </Col>
              <Col md={4}>
                <InputBox
                  type="text"
                  label="Role Name"
                  maxLength={100}
                  name="roleName"
                  value={roleObj.roleName}
                  onChange={handleChange}
                  required
                  setError={setError}
                  error={error}
                  submit={submit}
                  setFieldCount={setFieldCount}
                  fieldCount={fieldCount}
                  mandatoryFields={mandatoryFields}
                  setMandatoryFields={setMandatoryFields}
                />
              </Col>
              <Col md={4}>
                <SearchSelect
                  data={["Active", "Inactive"]}
                  name="status"
                  value={roleObj.status}
                  label="Status"
                  onChange={handleChange}
                  setError={setError}
                  error={error}
                  submit={submit}
                  setFieldCount={setFieldCount}
                  fieldCount={fieldCount}
                  mandatoryFields={mandatoryFields}
                  setMandatoryFields={setMandatoryFields}
                />
              </Col>
            </Row>

            <div className="tabledata-format">
              <Row className="select-all-row mb-3">
                <Form.Group controlId="SelectAll">
                  <Form.Check
                    type="checkbox"
                    className="checkbox-check"
                    id="SelectAll"
                    onChange={handleChangeSelectAll}
                    checked={selectAll}
                    label="Select All Menu Item"
                  />
                </Form.Group>
              </Row>

              <Row className="text-size mb-2">
                <Col lg={6} md={6} xs={9} className="menuhead-col">
                  <strong>Sessions</strong>
                </Col>
                <Col lg={6} md={6} xs={3} className="menuhead-col text-center">
                  <strong>Editable</strong>
                </Col>
              </Row>

              {Object.keys(menus).map((parentMenu, parentIndex) => {
                return menus[parentMenu]?.length > 0 ? (
                  <React.Fragment key={parentIndex}>
                    <Row key={parentMenu} className="parentmenu-row mb-2">
                      <Col lg={6} md={6} xs={9} className="menuhead-col">
                        <Form.Check
                          type="checkbox"
                          className="checkbox-check parent"
                          label={parentMenu}
                          checked={parent[parentMenu]}
                          onChange={() => {
                            handleParentChangeCheckbox(parentMenu, !parent[parentMenu]);
                          }}
                        />
                      </Col>
                      <Col lg={6} md={6} xs={3} className="text-center">
                        <Image
                          src={open[parentMenu] ? upArrowIcon : downarrowicon}
                          style={{ cursor: 'pointer', width: '24px', height: '24px' }}
                          onClick={() =>
                            setOpen(prev => ({
                              ...prev,
                              [parentMenu]: !prev[parentMenu]
                            }))
                          }
                        />
                      </Col>
                    </Row>
                    <Collapse in={open[parentMenu]}>
                      <div id={parentMenu}>
                        {menus[parentMenu].map((item, index) => (
                          <Row key={index} className="submenu-row mb-1">
                            <Col lg={5} md={5} xs={9} className="menuhead-col submenuhead-col">
                              <Form.Check
                                type="checkbox"
                                className="checkbox-check"
                                id={item.submenu}
                                label={item.submenu}
                                checked={item.read}
                                onChange={() => {
                                  handleChangeCheckbox(parentMenu, item.submenu, "read", !item.read);
                                }}
                              />
                            </Col>
                            <Col lg={7} md={7} xs={3} className="d-flex justify-content-center">
                              {item.DefaultWrite !== false && (
                                <Form.Check
                                  type="checkbox"
                                  className="checkbox-check"
                                  name={item.submenu + index}
                                  checked={item.write}
                                  onChange={() => {
                                    handleChangeCheckbox(parentMenu, item.submenu, "write", !item.write);
                                  }}
                                />
                              )}
                            </Col>
                          </Row>
                        ))}
                      </div>
                    </Collapse>
                  </React.Fragment>
                ) : null;
              })}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      
      {/* Confirmation Modal */}
      <Modal show={showConfirmation} onHide={handleCancelDeactivation} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Role Deactivation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          All the users associated with this role will be deactivated. Do you wish to continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDeactivation}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmDeactivation}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RoleForm;
